/**
 * @fileoverview Contains the core Service Worker logic for Vortex, which handles the initial request interception and handles client management for the Vortex service.
 */

import { FakeServiceWorker } from "@/worker/fakesw";
import { handleFetch } from "@/worker/fetch";
import BareClient from "@mercuryworkshop/bare-mux";
import { VortexConfig, VortexDB } from "@/types";
import { asyncSetWasm } from "@rewriters/wasm";
import { CookieStore } from "@/shared/cookie";
import { openDB } from "idb";
import { config, loadCodecs, setConfig } from "@/shared";
import { VortexDownload } from "@client/events";
export * from "./error";
export * from "./fetch";
export * from "./fakesw";

/**
 * Main `VortexServiceWorker` class created by the `$vortexLoadWorker` factory, which handles routing the proxy and contains the core logic for request interception.
 */
export class VortexServiceWorker extends EventTarget {
	/**
	 * `BareClient` instance to fetch requests under a chosen proxy transport.
	 */
	client: BareClient;
	/**
	 * Current VortexConfig saved in memory.
	 */
	config: VortexConfig;

	/**
	 * Recorded sync messages in the message queue.
	 */
	syncPool: Record<number, (val?: any) => void> = {};
	/**
	 * Current sync token for collected messages in the queue.
	 */
	synctoken = 0;

	/**
	 * Vortex's cookie jar for cookie emulation through other storage means, connected to a client.
	 */
	cookieStore = new CookieStore();

	/**
	 * Fake service worker registrations, so that some sites don't complain.
	 * This will eventually be replaced with a NestedSW feature under a flag in the future, but this will remain for stability even then.
	 */
	serviceWorkers: FakeServiceWorker[] = [];

	/**
	 * Initializes the `BareClient` Vortex uses to fetch requests under a chosen proxy transport, the cookie jar store for proxifying cookies, and inits the listeners for emulation features and dynamic configs set through the Vortex Controller.
	 */
	constructor() {
		super();
		this.client = new BareClient();

		(async () => {
			const db = await openDB<VortexDB>("$vortex", 1);
			const cookies = await db.get("cookies", "cookies");
			if (cookies) {
				this.cookieStore.load(cookies);
			}
		})();

		addEventListener("message", async ({ data }: { data: MessageC2W }) => {
			if (!("vortex$type" in data)) return;

			if ("vortex$token" in data) {
				// (ack message)
				const cb = this.syncPool[data.vortex$token];
				delete this.syncPool[data.vortex$token];
				cb(data);

				return;
			}

			if (data.vortex$type === "registerServiceWorker") {
				this.serviceWorkers.push(new FakeServiceWorker(data.port, data.origin));

				return;
			}

			if (data.vortex$type === "cookie") {
				this.cookieStore.setCookies([data.cookie], new URL(data.url));
				const db = await openDB<VortexDB>("$vortex", 1);
				await db.put("cookies", JSON.parse(this.cookieStore.dump()), "cookies");
			}

			if (data.vortex$type === "loadConfig") {
				this.config = data.config;
			}
		});
	}

	/**
	 * Dispatches a message in the message queues.
	 */
	async dispatch(client: Client, data: MessageW2C): Promise<MessageC2W> {
		const token = this.synctoken++;
		let cb: (val: MessageC2W) => void;
		const promise: Promise<MessageC2W> = new Promise((r) => (cb = r));
		this.syncPool[token] = cb;
		data.vortex$token = token;

		client.postMessage(data);

		return await promise;
	}

	/**
	 * Persists the current Vortex config into an IndexedDB store.
	 * Remember, this is because the Vortex config can be dynamically updated via the Vortex Controller APIs.
	 *
	 * @example
	 * self.addEventListener("fetch", async (ev) => {
	 *   await vortex.loadConfig();
	 *
	 *   ...
	 * });
	 */
	async loadConfig() {
		if (this.config) return;

		const db = await openDB<VortexDB>("$vortex", 1);
		this.config = await db.get("config", "config");

		if (this.config) {
			setConfig(this.config);
			await asyncSetWasm();
		}
	}

	/**
	 * Whether to route a request from a `FetchEvent` in Vortex.
	 *
	 * @example
	 * self.addEventListener("fetch", async (ev) => {
	 *   ...
	 *
	 *   if (vortex.route(ev)) {
	 *     ...
	 *   }
	 * });
	 * ```
	 */
	route({ request }: FetchEvent) {
		if (request.url.startsWith(location.origin + this.config.prefix))
			return true;
		else if (request.url.startsWith(location.origin + this.config.files.wasm))
			return true;
		else return false;
	}

	/**
	 * Handles a `FetchEvent` to be routed in Vortex.
	 * This is the heart of adding Vortex support to your web proxy.
	 *
	 * @example
	 * self.addEventListener("fetch", async (ev) => {
	 *   ...
	 *
	 *   if (vortex.route(ev)) {
	 *     ev.respondWith(vortex.fetch(ev));
	 *   }
	 * });
	 */
	async fetch({ request, clientId }: FetchEvent) {
		if (!this.config) await this.loadConfig();

		const client = await self.clients.get(clientId);

		return handleFetch.call(this, request, client);
	}
}

/**
 * Vortex fake Service Worker event message.
 * Contains a `vortex$type` for identifying the message.
 */
type RegisterServiceWorkerMessage = {
	vortex$type: "registerServiceWorker";
	port: MessagePort;
	origin: string;
};

/**
 * Vortex cookie jar event message.
 * Contains a `vortex$type` for identifying the message.
 */
type CookieMessage = {
	vortex$type: "cookie";
	cookie: string;
	url: string;
};

/**
 * Vortex config event message.
 * Contains a `vortex$type` for identifying the message.
 */
type ConfigMessage = {
	vortex$type: "loadConfig";
	config: VortexConfig;
};

/**
 * Vortex proxified download event message.
 * Contains a `vortex$type` for identifying the message.
 */
type DownloadMessage = {
	vortex$type: "download";
	download: VortexDownload;
};
/**
 * Default Vortex message.
 * Contains a `vortex$type` for identifying the message.
 */
type MessageCommon = {
	vortex$token?: number;
};

/**
 * Message types sent from the client to the Service Worker.
 * These are routed by their `vortex$type` to identify the messages apart from each other.
 */
type MessageTypeC2W =
	| RegisterServiceWorkerMessage
	| CookieMessage
	| ConfigMessage;
/**
 * w2c (types): Message types sent from the Service Worker to the client.
 */
type MessageTypeW2C = CookieMessage | DownloadMessage;

/** c2w: client to Service Worker */
export type MessageC2W = MessageCommon & MessageTypeC2W;
/** w2c: Service Worker to client */
export type MessageW2C = MessageCommon & MessageTypeW2C;
